import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import personnummer from 'personnummer.js';
import { AppContext, AppContextType } from '@/contexts/app-context';
import { Field } from '@/isomorphic/components/forms/components/field';
import { Button } from '@/isomorphic/components/typography/button/button';
import { validators } from '@/hooks/use-validation';
import { useLocalization } from '@/hooks/use-localization';
import { CountryType, KeyRole, KeyRoleType, Product, PropsType, SearchResult } from '../../types';
import { Circle } from '../circle';
import { useCorporateData } from '../../hooks/use-corporate-data';
import { LoginOverlay } from '@/isomorphic/components/login-overlay/login-overlay';
import { Loading } from '@/isomorphic/components/loading/loading';
import { getImageUrl } from '@/utils/images';
import { ChooseProducts } from './choose-product';
import { isUnder18 } from '@/utils/age-checks';

type Props = PropsType & {
    allowSoleTraders?: boolean;
    fetchProductInformation: boolean;
    user: AppContextType['user'];
    products: Product[] | null;
    isLoading: boolean;
    // Legacy Signicat passes the typed personnummer as the BankID login hint;
    // the oba-auth flow calls login() with no argument (pid comes from the
    // access token / /me).
    login: (ssn?: string | number) => void;
    // Per-block toggle. false (default) = legacy Signicat flow with the
    // up-front personnummer field; true = new oba-auth + PKCE flow without it.
    useObaAuth?: boolean;
    backLink?: {
        url: string;
        text: string;
    };
    setHasExistingApplication: Dispatch<SetStateAction<boolean>>;
};

export const Intro = ({
    kyc,
    user,
    login,
    fields,
    setStep,
    getLabel,
    products,
    isLoading,
    backLink,
    useObaAuth = false,
    setShowError,
    updateFields,
    getValuesFromStorage,
    fetchProductInformation,
    setHasExistingApplication,
    setCorporateRegistryDetails,
    allowSoleTraders = false,
}: Props) => {
    const [govId, setGovId] = useState('');
    const context = useContext(AppContext);
    const { getLanguage, getCountry } = useLocalization();
    const [isLoadingLoginHint, setIsLoadingLoginHint] = useState(false);
    const [showChooseProducts, setShowChooseProducts] = useState(false);
    const [hasLoadedApplicantData, setHasLoadedApplicantData] = useState(false);
    const {
        getCompanyInformation,
        getApplicantInformation,
        getCorporateRegistryDetails,
        searchCorporateRegistryApplications,
        getKycSurveyData,
        hasDataFetchError,
        isFetchingData,
        mapOrganizationTypeCodeToKycLabel,
    } = useCorporateData();

    const validateOrgNummer = (value: string) =>
        validators.companyRegistrationNumber(value, getLanguage(), {
            sv: { strict: !allowSoleTraders },
        });

    useEffect(() => {
        if (hasDataFetchError) {
            setShowError(true);
        }
    }, [hasDataFetchError, setShowError]);

    // oba-auth flow ONLY: after login, seed the applicant data from the auth
    // context. The bank service resolves `context.user.govid` (the real
    // personnummer) and `context.user.applicant` (name/email/phone) from
    // customer_service /me during /authenticate. There is no SSN field in this
    // flow.
    //
    // - If a profile came back (the normal /me path), seed govid + profile and
    //   unblock the loading gate immediately; no further lookup is needed.
    // - If only govid came back (profile missing), seed govid and leave the
    //   gate closed so the legacy address_lookup useEffect below fetches the
    //   profile using the now-real personnummer.
    //
    // The legacy Signicat flow does NOT use this effect: it gets the
    // personnummer from the SSN field the user types below.
    useEffect(() => {
        if (!useObaAuth || !user?.isAuthenticated || fields.applicant.governmentId.id) {
            return;
        }

        // No govid could be resolved server-side (e.g. the /government_id
        // lookup failed). Don't seed anything, but release the loading gate so
        // the user is never stuck on a permanent <Loading /> spinner.
        if (!context.user?.govid) {
            setHasLoadedApplicantData(true);
            return;
        }

        const profile = context.user?.applicant;
        const seededApplicant = {
            ...fields.applicant,
            governmentId: {
                ...fields.applicant.governmentId,
                id: context.user.govid,
            },
            ...(profile
                ? {
                      name: {
                          first: profile.firstName ?? fields.applicant.name.first,
                          last: profile.lastName ?? fields.applicant.name.last,
                      },
                      email: profile.email ? profile.email.toLocaleLowerCase() : fields.applicant.email,
                      phoneNumber: profile.phoneNumber ?? fields.applicant.phoneNumber,
                  }
                : {}),
        };

        updateFields({ ...fields, applicant: seededApplicant });

        // Profile already populated, unblock now. If no profile came back,
        // leave the gate closed so the legacy address_lookup useEffect below
        // runs with the seeded personnummer.
        if (profile) {
            setHasLoadedApplicantData(true);
        }
    }, [useObaAuth, user?.isAuthenticated, context.user?.govid, context.user?.applicant, fields.applicant.governmentId.id]);

    // Fetch the applicant profile from address_lookup_service once we have the
    // personnummer in form state. This is the original legacy Signicat
    // behaviour (govid typed into the SSN field) and is ALSO reused by the
    // oba-auth flow when /me returned a govid but no profile. The normal
    // oba-auth /me path never reaches here because it already set
    // hasLoadedApplicantData.
    useEffect(() => {
        if (user?.isAuthenticated && fields.applicant.governmentId.id && !hasLoadedApplicantData) {
            fetchUserData(fields.applicant.governmentId.id);
        }
    }, [user?.isAuthenticated, fields.applicant.governmentId.id, hasLoadedApplicantData]);

    useEffect(() => {
        if (fields.company.governmentId.id) {
            setGovId(fields.company.governmentId.id);
        }
    }, [fields.company.governmentId.id]);

    // Legacy Signicat flow: keeps the typed personnummer in form state so it
    // can be sent as the BankID login hint and reused for the /govid lookup.
    const updateSsn = (value: string) =>
        updateFields({
            ...fields,
            applicant: {
                ...fields.applicant,
                governmentId: {
                    ...fields.applicant.governmentId,
                    id: value,
                },
            },
        });

    const capitalize = (value: string) => value?.charAt(0)?.toLocaleUpperCase() + value?.slice(1)?.toLocaleLowerCase();

    async function fetchUserData(govId: string) {
        const response = await getApplicantInformation(govId);
        setHasLoadedApplicantData(true);
        if (!response) {
            return;
        }

        const first = response?.names?.firstNames?.length ? response?.names?.firstNames.map(capitalize).join(' ') : fields.applicant.name.first;
        const last = response?.names?.familyName ? capitalize(response.names.familyName) : fields.applicant.name.last;

        const applicant = {
            ...fields.applicant,
            name: {
                first,
                last,
            },
            email: response?.email ? response.email.toLocaleLowerCase() : fields.applicant.email,
            phoneNumber: response.phoneNumbers?.[0]?.number ?? fields.applicant.phoneNumber,
        };

        updateFields({ applicant });
    }

    async function fetchData(govId: string) {
        const company = { ...fields.company };
        let allKeyRoles: KeyRoleType[] = [];
        company.governmentId.id = govId;

        const [kycSurveyData, companyInformation, corporateRegistryDetails, corporateRegistryApplications] = await Promise.all([
            getKycSurveyData(),
            getCompanyInformation(govId),
            getCorporateRegistryDetails(govId),
            searchCorporateRegistryApplications(govId),
        ]);

        if (corporateRegistryApplications === SearchResult.APPLICATION_FOUND) {
            setHasExistingApplication(true);
            return;
        }

        if (companyInformation?.addresses?.length) {
            const address = companyInformation.addresses[0];
            company.address = {
                street: address.streetNameWithNumber,
                city: address.city,
                zipCode: address.postalCode,
                // This is intentionally _not_ address.countryCode and is hard-coded
                // on purpose for now. This was resurs bank's decision!
                countryCode: address.countryCode ?? (getCountry().toLocaleUpperCase() as CountryType),
            };
        }

        if (companyInformation?.phoneNumbers?.length) {
            company.phoneNumber = companyInformation.phoneNumbers[0].number;
        }

        if (corporateRegistryDetails) {
            const { allSignatories, beneficialOwners, combinations, organizationName, organizationTypeCode } = corporateRegistryDetails;
            const corporateData = { allSignatories, beneficialOwners, combinations };
            const storageValue = getValuesFromStorage();

            if (!combinations.length || !allSignatories.length) {
                setShowError(true);
                return;
            }

            // Föreningar used to be blocked here (money-laundering risk). Removed
            // 2026-11 at Resurs's request: all föreningar may apply online.
            // Paper trail: docs/handover/08-external-apis.md, "Föreningar may apply online".

            setCorporateRegistryDetails(corporateData);

            company.organizationName = organizationName;
            // The corporate registry returns a raw bolagsformkod (e.g. "BAB"),
            // but the Bolagsform <Select> on step one is driven by KYC question
            // 103, whose options are a fixed set of labels. Translate the code
            // to the matching label so the field pre-fills correctly. Unknown
            // codes resolve to '' (field left empty and editable).
            company.organizationType = mapOrganizationTypeCodeToKycLabel(organizationTypeCode);

            allKeyRoles = allSignatories.map(({ name, governmentId }) => {
                const storedKeyRole = storageValue?.keyRoles?.find(keyRole => keyRole.governmentId?.id === governmentId.id);

                return {
                    name,
                    governmentId,
                    isEditable: false,
                    email: storedKeyRole?.email ?? '',
                    roles: storedKeyRole?.roles ?? [KeyRole.SIGNATORY],
                    phoneNumber: storedKeyRole?.phoneNumber ?? '',
                };
            });

            const keyRoleIds = allKeyRoles.map(role => role.governmentId.id);
            const newStoredKeyRoles = storageValue?.keyRoles?.filter(keyRole => !keyRoleIds.includes(keyRole.governmentId.id)) ?? [];
            allKeyRoles = [...allKeyRoles, ...newStoredKeyRoles].filter(r => r);

            if (kycSurveyData) {
                kyc.updateKycSurvyData(kycSurveyData, corporateData, company);
            }
        }

        updateFields({ company, allKeyRoles });
    }

    const onGovIdUpdate = (govId: string) => {
        if (context?.user?.isAuthenticated && validateOrgNummer(govId)) {
            fetchData(govId);
        }
    };

    useEffect(() => {
        onGovIdUpdate(govId);
    }, [govId]);

    // Legacy Signicat flow only: gates the BankID button on a valid (and 18+)
    // personnummer typed into the SSN field. Unused in the oba-auth flow.
    const isLoginButtonDisabled = useMemo(() => {
        if (isLoadingLoginHint) {
            return true;
        }

        if (getCountry() !== 'se') {
            return !validators.socialSecurityNumber(fields.applicant.governmentId.id, getLanguage());
        }

        try {
            const parsed = personnummer.parse(fields.applicant.governmentId.id);
            if (!parsed.valid) {
                return true;
            }

            if ('date' in parsed && parsed.date) {
                return isUnder18(parsed.date);
            }

            return false;
        } catch (error) {
            console.error(error);
        }

        return false;
    }, [fields.applicant.governmentId, isLoadingLoginHint, getCountry, getLanguage]);

    const hasUnder18Error = useMemo(() => {
        const parsed = personnummer.parse(fields.applicant.governmentId.id);
        if (!parsed.valid) {
            return false;
        }

        if ('date' in parsed && parsed.date) {
            return isUnder18(parsed.date);
        }

        return false;
    }, [fields.applicant.governmentId]);

    const isApplyButtonDisabled = useMemo(() => {
        if (isFetchingData) {
            return true;
        }

        return !validateOrgNummer(govId);
    }, [govId, isFetchingData]);

    if (context?.user?.isAuthenticated && !hasLoadedApplicantData) {
        return (
            <div className="r-flex r-min-h-72 r-flex-col r-items-center r-justify-center" role="status">
                <Loading />
            </div>
        );
    }

    if (!context.user?.isAuthenticated) {
        return (
            <LoginOverlay
                backLink={backLink}
                mobileImageHeader={getLabel('businessLogIn')}
                mobileImage={getImageUrl('/inlaning/company-bank-large.jpg', '500xAUTO')}
                desktopImage={getImageUrl('/inlaning/company-bank-large.jpg', '1920xAUTO')}
            >
                <div className="r-space-y-4">
                    <div className="r-space-y-4 max-sm:r-hidden">
                        <h2 className="r-font-medium">{getLabel('savingsAccountBusiness')}</h2>
                        <h3 className="r-pb-2 r-text-3xl r-font-medium">{getLabel('openSavingsAccount')}</h3>
                    </div>
                    {useObaAuth ? (
                        // New oba-auth flow: no personnummer is collected here;
                        // BankID is started without a login hint.
                        <>
                            <div dangerouslySetInnerHTML={{ __html: getLabel('loginIntroText') }} />
                            <Button fullWidth onClick={() => login()} variant="bankidwhite">
                                {getLabel('identifyWithBankId')}
                            </Button>
                        </>
                    ) : (
                        // Legacy Signicat flow: collect the personnummer up
                        // front and pass it as the BankID login hint.
                        <>
                            <Field
                                onChange={updateSsn}
                                label={getLabel('socialSecurityNumber')}
                                value={fields.applicant.governmentId.id}
                                validation={value => validators.socialSecurityNumber(value, getLanguage())}
                            />
                            {hasUnder18Error ? (
                                <div className="r-py-4">
                                    <div
                                        className="r-border-l-8 r-border-l-red-600 r-pl-4"
                                        dangerouslySetInnerHTML={{ __html: getLabel('under18ErrorText') }}
                                    />
                                </div>
                            ) : null}
                            <Button
                                fullWidth
                                disabled={isLoginButtonDisabled}
                                onClick={() => {
                                    setIsLoadingLoginHint(true);
                                    login(fields.applicant.governmentId.id);
                                }}
                                variant="bankidwhite"
                            >
                                {getLabel('identifyWithBankId')}
                            </Button>
                        </>
                    )}
                    <div className="r-pt-2" dangerouslySetInnerHTML={{ __html: getLabel('loginPageText') }} />
                </div>
            </LoginOverlay>
        );
    }

    if (showChooseProducts) {
        return (
            <ChooseProducts
                getLabel={getLabel}
                isLoading={isLoading}
                products={products}
                fields={fields}
                setStep={setStep}
                updateFields={updateFields}
            />
        );
    }

    return (
        <div className="r-relative r-max-w-xl r-space-y-4 r-py-4 md:r-py-16 lg:r-mx-auto">
            {!fetchProductInformation ? (
                <div className="r-w-44 md:r-absolute md:-r-right-[200px]">
                    <Circle getLabel={getLabel} fetchProductInformation={fetchProductInformation} products={products} fields={fields} />
                </div>
            ) : null}

            <h2 className="r-font-medium">{getLabel('savingsAccountBusiness')}</h2>
            <h3 className="r-pb-2 r-text-3xl r-font-medium">{getLabel('welcomeUser', fields.applicant.name.first)}</h3>
            <div className="r-space-y-4 r-pb-2" dangerouslySetInnerHTML={{ __html: getLabel('introductoryText') }} />

            <form
                className="r-space-y-2"
                onSubmit={e => {
                    e.preventDefault();
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    fetchProductInformation ? setShowChooseProducts(true) : setStep(1);
                }}
            >
                <p>
                    <strong>{getLabel('provideYourOrgNumber')}</strong>
                </p>
                <Field label={getLabel('orgNumber')} value={govId} onChange={setGovId} validation={validateOrgNummer} />
                {fields.company.organizationName ? <p className="r-font-bold r-text-primary">{fields.company.organizationName}</p> : null}
                <div className="r-pt-4">
                    <Button disabled={isApplyButtonDisabled} className="max-md:r-w-full" isSubmit>
                        {fetchProductInformation ? getLabel('chooseSavingsAccount') : getLabel('openSavingsAccount')}
                    </Button>
                </div>
            </form>
        </div>
    );
};
